import { ChangeEvent, FC, useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  Chip,
} from "@mui/material";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import {
  GetInsuranceTransactionDto,
  ResponseExportInfoDtoBillingCenter,
} from "../../../../../../../api-client";
import { formatDateUI } from "../../../../../../../helpers/dateFormat";
import {
  useUpdateExportedStatus,
  useUpdateDelayStatus,
} from "../../../../../../../stores/Transactions/transaction.mutations";
import { useUserRole } from "../../../../../../../stores/User";
import { BillingStatus } from "../../../pages/InsurancePageLayout";
import { ExportStatusConfirmDialog } from "./ExportStatusConfirmDialog";
import useStore from "../../../../../../../helpers/useStore";
import { TransactionsContext } from "../../../../../../../stores/Transactions/Transactions.provider";

interface TransactionHeaderProps {
  transaction: GetInsuranceTransactionDto;
  billingCenter: ResponseExportInfoDtoBillingCenter | null;
  onClose?: () => void;
}

export const TransactionHeader: FC<TransactionHeaderProps> = ({
  transaction,
  billingCenter,
  onClose,
}) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [wasExportedValue, setWasExportedValue] = useState(false);

  // Delay status local state (tracks UI independently from stale props)
  const [isCurrentlyDelayed, setIsCurrentlyDelayed] = useState(transaction.isDelayed ?? false);
  const [currentAmountDelayed, setCurrentAmountDelayed] = useState<number | null>(
    transaction.amountDelayed ?? null,
  );
  const [delayAmount, setDelayAmount] = useState<string>(
    transaction.amountDelayed != null ? String(transaction.amountDelayed) : String(transaction.amount),
  );

  const transactionStore = useStore(TransactionsContext);
  const { transactionRefresh, setTransactionRefresh } = transactionStore;

  const updateExportStatusMutate = useUpdateExportedStatus();
  const updateDelayStatusMutate = useUpdateDelayStatus();
  const userRoles = useUserRole();
  const isChief = userRoles?.some((role) => role.id === "chief");

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setWasExportedValue(event.target.checked);
    setConfirmDialogOpen(true);
  };

  const handleConfirmUpdate = () => {
    updateExportStatusMutate.mutate(
      {
        transactionId: transaction.id,
        updateData: {
          wasExported: false,
          billStatus: BillingStatus.START,
        },
      },
      {
        onSuccess: () => {
          // Toggle transactionRefresh to trigger table refresh
          setTransactionRefresh(!transactionRefresh);
          setConfirmDialogOpen(false);
          onClose?.();
        },
      },
    );
  };

  const handleCancelUpdate = () => {
    setWasExportedValue(!wasExportedValue);
    setConfirmDialogOpen(false);
  };

  const handleSetDelayed = () => {
    const amount = parseFloat(delayAmount);
    if (isNaN(amount) || amount <= 0) return;
    updateDelayStatusMutate.mutate(
      {
        transactionId: transaction.id,
        updateData: { isDelayed: true, amountDelayed: amount },
      },
      {
        onSuccess: () => {
          setIsCurrentlyDelayed(true);
          setCurrentAmountDelayed(amount);
          setTransactionRefresh(!transactionRefresh);
        },
      },
    );
  };

  const handleClearDelayed = () => {
    updateDelayStatusMutate.mutate(
      {
        transactionId: transaction.id,
        updateData: { isDelayed: false },
      },
      {
        onSuccess: () => {
          setIsCurrentlyDelayed(false);
          setCurrentAmountDelayed(null);
          setTransactionRefresh(!transactionRefresh);
        },
      },
    );
  };

  return (
    <Paper
      sx={{
        p: 0.5,
        borderRadius: 2,
        bgcolor: "#D9DFE2",
        border: "1px solid #e0e0e0",
      }}
    >
      <Grid container spacing={0.5}>
        {/* Transaction Details */}
        <Grid item xs={12}>
          <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Rechnungsnummer
              </Typography>
              <Typography variant="body2" fontWeight={500} noWrap>
                {transaction.transactionNumber}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Kostenträger - IK
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ wordBreak: "break-word" }}
              >
                {transaction.payer.name} - {transaction.payer.ik}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Mandant
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ wordBreak: "break-word" }}
              >
                {transaction.account.name}
              </Typography>
            </Box>
            {billingCenter && (
              <>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    Abrechnungszentrum - IK
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ wordBreak: "break-word" }}
                  >
                    {billingCenter.name} - {billingCenter.ik}
                  </Typography>
                </Box>

                {billingCenter.address && (
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      Adresse
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ wordBreak: "break-word" }}
                    >
                      {billingCenter.address.street},{" "}
                      {billingCenter.address.zip} {billingCenter.address.city}
                    </Typography>
                  </Box>
                )}
              </>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Gesamtpreis
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                color="#9C1A35"
                noWrap
              >
                {Number(transaction.amount).toFixed(2).replace(".", ",")} €
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary">
                Datum
              </Typography>
              <Typography variant="body2" fontWeight={500} noWrap>
                {formatDateUI(transaction.createdAt)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={wasExportedValue}
                    onChange={handleCheckboxChange}
                    disabled={!isChief || updateExportStatusMutate.isPending}
                    sx={{
                      color: "#bdbdbd",
                      "&.Mui-checked": {
                        color: "#9C1A35",
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="caption" color="text.secondary">
                    Export aufheben
                  </Typography>
                }
              />
            </Box>
          </Box>
        </Grid>

        {/* Delay Status Row */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              pt: 0.5,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            {isCurrentlyDelayed ? (
              <>
                <Chip
                  icon={<PauseCircleOutlineIcon />}
                  label={`Verzögert: ${Number(currentAmountDelayed || 0).toFixed(2).replace(".", ",")} €`}
                  color="error"
                  size="small"
                  variant="outlined"
                />
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<PlayCircleOutlineIcon />}
                  onClick={handleClearDelayed}
                  disabled={updateDelayStatusMutate.isPending}
                >
                  Verzögerung aufheben
                </Button>
              </>
            ) : (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  Verzögerung markieren:
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  label="Betrag (€)"
                  value={delayAmount}
                  onChange={(e) => setDelayAmount(e.target.value)}
                  sx={{ width: 120 }}
                  inputProps={{ min: 0, step: "0.01" }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PauseCircleOutlineIcon />}
                  onClick={handleSetDelayed}
                  disabled={updateDelayStatusMutate.isPending || !delayAmount}
                  sx={{ color: "#555", borderColor: "#999" }}
                >
                  Als verzögert markieren
                </Button>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <ExportStatusConfirmDialog
        open={confirmDialogOpen}
        onClose={handleCancelUpdate}
        onConfirm={handleConfirmUpdate}
        transactionNumber={transaction.transactionNumber}
        isPending={updateExportStatusMutate.isPending}
      />
    </Paper>
  );
};
